/**
 * Payroll calculation utilities.
 * Saudi (Qawaem): GOSI + SANED, no salary income tax.
 * Legacy Algeria helpers retained for non-Saudi builds.
 */
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { calculateSaudiPayrollSummary } from '../../pages/salaires/utils/payrollSaudiFormat';

/**
 * Calcule le coefficient prorata IRG basé sur le nombre de jours travaillés et le nombre de jours ouvrables
 * Formule: Coefficient_prorata_IRG = Nombre_de_Jours_travaillés/Nombre_de_Jours_ouvrables
 * 
 * @param {number} joursTravailes - Nombre de jours travaillés dans le mois
 * @param {number} joursOuvrables - Nombre de jours ouvrables dans le mois
 * @returns {number} - Coefficient prorata IRG (entre 0 et 1)
 */
export const calculerCoefficientProrataIRG = (joursTravailes, joursOuvrables) => {
  // Vérification des valeurs
  if (!joursTravailes || !joursOuvrables || joursOuvrables <= 0) {
    return 1; // Valeur par défaut en cas de données invalides
  }
  
  // Calcul du coefficient
  const coefficient = joursTravailes / joursOuvrables;
  
  // Limiter le coefficient entre 0 et 1
  return Math.min(Math.max(coefficient, 0), 1);
};

/**
 * Calcule le salaire imposable mensuel en utilisant le coefficient prorata IRG
 * Formule: Salaire_imposable_mois = Salaire_Imposable_perçu / Coefficient_prorata_IRG
 * 
 * @param {number} salaireImposablePercu - Salaire imposable perçu pour la période
 * @param {number} coefficientProrataIRG - Coefficient prorata IRG
 * @returns {number} - Salaire imposable mensuel
 */
export const calculerSalaireImposableMensuel = (salaireImposablePercu, coefficientProrataIRG) => {
  // Vérification des valeurs
  if (!salaireImposablePercu) {
    return 0;
  }
  
  if (!coefficientProrataIRG || coefficientProrataIRG <= 0) {
    return salaireImposablePercu; // Pas de proratisation si coefficient invalide
  }
  
  // Calcul du salaire imposable mensuel (ramené au mois complet)
  return salaireImposablePercu / coefficientProrataIRG;
};

/**
 * Calcule la retenue IRG réelle en utilisant le coefficient prorata IRG
 * Formule: Retenue_IRG_Réelle = Retenue_IRG_mensuel x Coefficient_prorata_IRG
 * 
 * @param {number} retenueIRGMensuel - Retenue IRG mensuelle (basée sur le salaire imposable mensuel)
 * @param {number} coefficientProrataIRG - Coefficient prorata IRG
 * @returns {number} - Retenue IRG réelle pour la période
 */
export const calculerRetenueIRGReelle = (retenueIRGMensuel, coefficientProrataIRG) => {
  // Vérification des valeurs
  if (!retenueIRGMensuel) {
    return 0;
  }
  
  if (!coefficientProrataIRG || coefficientProrataIRG < 0) {
    return retenueIRGMensuel; // Pas de proratisation si coefficient invalide
  }
  
  // Calcul de la retenue IRG réelle
  return retenueIRGMensuel * coefficientProrataIRG;
};

/**
 * Calcule l'IRG (Impôt sur le Revenu Global) selon le barème IRG 2022
 * 
 * @param {number} salaireImposable - Salaire imposable
 * @param {number} coefficientProrataIRG - Coefficient prorata IRG (par défaut: 1 pour un mois complet)
 * @param {Array} bareme - Barème IRG à appliquer
 * @param {Object} options - Options supplémentaires pour le calcul
 * @returns {number} - Montant de l'IRG à retenir
 */
export const calculerIRG = (salaireImposable, coefficientProrataIRG = 1, bareme, options = {}) => {
  // Options par défaut
  const { 
    abattementSud = 0,        // Abattement pour les régions du sud (25% ou 50%)
    abattementHandicape = 0,  // Abattement pour les handicapés (40%)
    exonere = false,          // Exonération complète
    abattementAutre = 0       // Autre type d'abattement (en pourcentage)
  } = options;
  
  // Vérification des valeurs
  if (!salaireImposable || salaireImposable <= 0 || !bareme || !Array.isArray(bareme) || bareme.length === 0) {
    return 0; // Pas d'IRG si données invalides
  }
  
  // Gestion des exonérations
  if (exonere) {
    return 0; // Exonéré d'IRG
  }
  
  // Calcul du salaire imposable mensuel (ramené au mois complet)
  const salaireImposableMensuel = calculerSalaireImposableMensuel(salaireImposable, coefficientProrataIRG);
  
  // Trouver la tranche applicable dans le barème
  const trancheApplicable = bareme.find(tranche => 
    salaireImposableMensuel > tranche.min && salaireImposableMensuel <= tranche.max
  );
  
  if (!trancheApplicable) {
    return 0; // Aucune tranche applicable
  }
  
  // Calcul de l'IRG mensuel selon la formule: (salaire * taux / 100) - déduction
  let retenueIRGMensuel = ((salaireImposableMensuel * trancheApplicable.taux) / 100) - trancheApplicable.deduction;
  
  // Appliquer les abattements si nécessaire
  if (abattementSud > 0) {
    retenueIRGMensuel = retenueIRGMensuel * (1 - (abattementSud / 100));
  }
  
  if (abattementHandicape > 0) {
    retenueIRGMensuel = retenueIRGMensuel * (1 - (abattementHandicape / 100));
  }
  
  if (abattementAutre > 0) {
    retenueIRGMensuel = retenueIRGMensuel * (1 - (abattementAutre / 100));
  }
  
  // Calcul de la retenue IRG réelle en tenant compte du coefficient prorata
  const retenueIRGReelle = calculerRetenueIRGReelle(retenueIRGMensuel, coefficientProrataIRG);
  
  // Arrondir à l'entier inférieur (selon la pratique algérienne)
  return Math.floor(Math.max(retenueIRGReelle, 0));
};

/**
 * Calcule les jours ouvrables dans un mois donné (jours du mois - weekends - jours fériés)
 * 
 * @param {Date} date - Date pour laquelle calculer les jours ouvrables
 * @param {Array} joursFeries - Liste des jours fériés (format Date)
 * @returns {number} - Nombre de jours ouvrables
 */
export const calculerJoursOuvrables = (date, joursFeries = []) => {
  const annee = date.getFullYear();
  const mois = date.getMonth();
  const nombreJoursMois = new Date(annee, mois + 1, 0).getDate();
  
  let joursOuvrables = 0;
  
  // Parcourir tous les jours du mois
  for (let jour = 1; jour <= nombreJoursMois; jour++) {
    const dateJour = new Date(annee, mois, jour);
    const jourSemaine = dateJour.getDay();
    
    // Vérifier si ce n'est pas un weekend (0 = dimanche, 6 = samedi)
    const estWeekend = jourSemaine === 0 || jourSemaine === 6;
    
    // Vérifier si ce n'est pas un jour férié
    const estJourFerie = joursFeries.some(jourFerie => 
      jourFerie.getDate() === dateJour.getDate() && 
      jourFerie.getMonth() === dateJour.getMonth() && 
      jourFerie.getFullYear() === dateJour.getFullYear()
    );
    
    // Incrémenter le compteur si c'est un jour ouvrable
    if (!estWeekend && !estJourFerie) {
      joursOuvrables++;
    }
  }
  
  return joursOuvrables;
};

/**
 * Calcule le total des cotisations sociales selon les normes algériennes
 * 
 * @param {number} salaireCotisable - Salaire cotisable
 * @param {Object} tauxCotisations - Objet contenant les différents taux de cotisation
 * @param {Object} options - Options supplémentaires pour le calcul
 * @returns {Object} - Objet avec les montants des différentes cotisations
 */
export const calculerCotisationsSociales = (salaireCotisable, tauxCotisations, options = {}) => {
  if (isSaudiBrand()) {
    const employee = options.employee || { estEtranger: options.estEtranger };
    const summary = calculateSaudiPayrollSummary(employee, salaireCotisable, {});
    return {
      securiteSocialeSalarie: summary.retenueGOSISalarie + summary.retenueSANEDSalarie,
      securiteSocialeEmployeur: summary.retenueGOSIEmployeur + summary.retenueSANEDEmployeur,
      cacobatCongesPayes: summary.provisionEOSB,
      intemperiesSalarie: 0,
      intemperiesEmployeur: 0,
      mutuelle: 0,
      totalSalarie: summary.retenueSSSalarie,
      totalEmployeur: summary.retenueSSEmployeur,
      total: summary.retenueSSSalarie + summary.retenueSSEmployeur,
      gosi: summary.gosi,
      saned: summary.saned,
    };
  }

  const {
    estEtranger = false,
    modeCNAS = 0, // 0 = Standard, 1 = ANEM 7%, 2 = ANEM 16%, 3 = Autre
    cotiseIntemperies = false,
    cotiseMutuelle = false,
    tauxMutuelle = 1.5,
    cotiseCacobatph = true,
  } = options;

  // Vérifier si le salarié est éligible aux cotisations
  if (!salaireCotisable || salaireCotisable <= 0) {
    return {
      securiteSocialeSalarie: 0,
      securiteSocialeEmployeur: 0,
      cacobatCongesPayes: 0,
      intemperiesSalarie: 0,
      intemperiesEmployeur: 0,
      mutuelle: 0,
      totalSalarie: 0,
      totalEmployeur: 0,
      total: 0
    };
  }

  // Déterminer les taux applicables selon le mode CNAS
  let tauxSalarie = tauxCotisations.securiteSocialeSalarie || 9;
  let tauxEmployeur = tauxCotisations.securiteSocialeEmployeur || 26;

  if (modeCNAS === 1) { // ANEM 7%
    tauxEmployeur = 7;
  } else if (modeCNAS === 2) { // ANEM 16%
    tauxEmployeur = 16 - tauxSalarie; // 16% total, on soustrait la part salarié
  }

  // Calcul des cotisations de base
  const securiteSocialeSalarie = (salaireCotisable * tauxSalarie) / 100;
  const securiteSocialeEmployeur = (salaireCotisable * tauxEmployeur) / 100;
  const cacobatCongesPayes = cotiseCacobatph ? (salaireCotisable * (tauxCotisations.cacobatCongesPayes || 0.75)) / 100 : 0;

  // Cotisations intempéries (BTP)
  const intemperiesSalarie = cotiseIntemperies ? (salaireCotisable * (tauxCotisations.intemperiesSalarie || 0.75)) / 100 : 0;
  const intemperiesEmployeur = cotiseIntemperies ? (salaireCotisable * (tauxCotisations.intemperiesEmployeur || 0.25)) / 100 : 0;

  // Cotisation mutuelle
  const mutuelle = cotiseMutuelle ? (salaireCotisable * tauxMutuelle) / 100 : 0;

  // Calculs des totaux
  const totalSalarie = securiteSocialeSalarie + intemperiesSalarie + mutuelle;
  const totalEmployeur = securiteSocialeEmployeur + cacobatCongesPayes + intemperiesEmployeur;
  const total = totalSalarie + totalEmployeur;

  return {
    securiteSocialeSalarie,
    securiteSocialeEmployeur,
    cacobatCongesPayes,
    intemperiesSalarie,
    intemperiesEmployeur,
    mutuelle,
    totalSalarie,
    totalEmployeur,
    total
  };
};

/**
 * Calcule le salaire net après déduction des cotisations sociales et de l'IRG
 * 
 * @param {number} salaireBrut - Salaire brut
 * @param {number} cotisationsSalarie - Montant des cotisations salarié
 * @param {number} irg - Montant de l'IRG
 * @returns {number} - Salaire net
 */
export const calculerSalaireNet = (salaireBrut, cotisationsSalarie, irg) => {
  return salaireBrut - cotisationsSalarie - irg;
};

/**
 * Calcule les droits au congé pour un mois
 * Selon le document CALCUL_DES_CONGES_DANS_PCPAIE.htm
 * 
 * @param {number} salaireCotisable - Salaire cotisable (R500)
 * @param {number} salaireImposable - Salaire imposable (R652)
 * @param {number} indemniteTransport - Indemnité de transport (R510)
 * @param {Object} options - Options supplémentaires
 * @returns {Object} - Informations sur les droits au congé
 */
export const calculerDroitsCongeMensuel = (salaireCotisable, salaireImposable, indemniteTransport = 0, options = {}) => {
  // Nombre de jours acquis par mois (par défaut 2.5 jours)
  const nombreJoursAcquis = options.nombreJoursAcquis || 2.5;

  // Base de calcul du congé
  let congeCotisable = 0;
  let congeImposable = 0;
  let congeNonCotisable = 0;

  // Méthode 1: Basée sur le salaire cotisable (R500/12)
  if (options.baseSurCotisable) {
    congeCotisable = salaireCotisable / 12;
    congeImposable = congeCotisable;
  } 
  // Méthode 2: Basée sur le salaire imposable ((R652+R510)/12)
  else if (options.baseSurImposable) {
    congeImposable = (salaireImposable + indemniteTransport) / 12;
    // Partie cotisable du congé
    congeCotisable = salaireCotisable / 12;
    // Partie non cotisable (mais imposable)
    congeNonCotisable = congeImposable - congeCotisable;
  }
  // Par défaut: Salaire cotisable
  else {
    congeCotisable = salaireCotisable / 12;
    congeImposable = congeCotisable;
  }

  return {
    nombreJoursAcquis,
    congeCotisable,
    congeImposable,
    congeNonCotisable
  };
};

/**
 * Calcule l'indemnité de congé à payer
 * 
 * @param {number} nombreJoursCongePris - Nombre de jours de congé pris
 * @param {number} nombreJoursCongeRestant - Nombre de jours de congé restants
 * @param {number} congeCotisableRestant - Montant cotisable restant
 * @param {number} congeImposableRestant - Montant imposable restant
 * @param {boolean} congeComplet - Si le salarié prend son congé sur un mois complet
 * @returns {Object} - Indemnité de congé
 */
export const calculerIndemniteConge = (nombreJoursCongePris, nombreJoursCongeRestant, congeCotisableRestant, congeImposableRestant, congeComplet = false) => {
  // Si congé sur un mois complet, on prend tout le reste
  if (congeComplet) {
    return {
      nombreJoursCongePris: nombreJoursCongeRestant,
      congeCotisable: congeCotisableRestant,
      congeImposable: congeImposableRestant,
      congeNonCotisable: congeImposableRestant - congeCotisableRestant
    };
  }

  // Si congé partiel, on calcule au prorata
  if (nombreJoursCongePris <= 0 || nombreJoursCongeRestant <= 0) {
    return {
      nombreJoursCongePris: 0,
      congeCotisable: 0,
      congeImposable: 0,
      congeNonCotisable: 0
    };
  }

  // Limiter le nombre de jours à prendre au nombre de jours restants
  const joursPris = Math.min(nombreJoursCongePris, nombreJoursCongeRestant);
  
  // Calcul prorata
  const ratio = joursPris / nombreJoursCongeRestant;
  const congeCotisable = congeCotisableRestant * ratio;
  const congeImposable = congeImposableRestant * ratio;
  
  return {
    nombreJoursCongePris: joursPris,
    congeCotisable,
    congeImposable,
    congeNonCotisable: congeImposable - congeCotisable
  };
};

/**
 * Calcule la régularisation de l'IRG selon le mode de calcul algérien
 * Utilisé pour les rappels de salaire
 * Basé sur le document CALCUL_DES_RAPPELS.htm
 * 
 * @param {number} salaireImposableInitial - Salaire imposable initial
 * @param {number} salaireImposableCorrige - Salaire imposable corrigé
 * @param {Array} bareme - Barème IRG à appliquer
 * @param {number} coefficientProrataIRG - Coefficient prorata IRG (par défaut: 1 pour un mois complet)
 * @returns {Object} - Informations sur la régularisation de l'IRG
 */
export const calculerRegularisationIRG = (salaireImposableInitial, salaireImposableCorrige, bareme, coefficientProrataIRG = 1) => {
  // Calcul IRG initial
  const irgInitial = calculerIRG(salaireImposableInitial, coefficientProrataIRG, bareme);
  
  // Calcul IRG corrigé
  const irgCorrige = calculerIRG(salaireImposableCorrige, coefficientProrataIRG, bareme);
  
  // Différence (peut être positive ou négative)
  const difference = irgCorrige - irgInitial;
  
  return {
    irgInitial,
    irgCorrige,
    difference,
    type: difference > 0 ? 'trop-percu' : difference < 0 ? 'moins-percu' : 'aucun'
  };
}; 

/**
 * Calcule les éléments du Salaire Cotisable/Imposable selon les paramètres définis
 * Utilise les données de l'employé, du pointage et les paramètres de calcul
 * 
 * @param {Object} employee - Données de l'employé
 * @param {Object} pointage - Données de pointage (jours travaillés, heures supplémentaires, etc.)
 * @param {Object} settings - Paramètres de calcul (primes, indemnités, formules)
 * @returns {Object} - Objet contenant tous les éléments du Salaire Cotisable/Imposable
 */
export const calculerSalaireCotisableImposable = (employee, pointage, settings) => {
  // Valeurs par défaut si non fournies
  const employeeData = employee || {};
  const pointageData = pointage || {
    nbrJoursMois: 30,
    nbrJoursOuvrables: 22,
    nbrJoursTravailles: 22,
    nbrJoursSupplementaires: 0,
    nbrHeuresSupplementaires: 0
  };
  const settingsData = settings || { remuneration: {} };
  
  // Récupérer les paramètres de rémunération
  const remuneration = settingsData.remuneration || {};
  const primesIndemnites = remuneration.primesIndemnites || {};
  
  // Salaire de base et taux journalier/horaire
  const salaireBase = employeeData.salaireBase || 0;
  const tauxJournalier = pointageData.nbrJoursOuvrables > 0 
    ? salaireBase / pointageData.nbrJoursOuvrables 
    : 0;
  const tauxHoraire = pointageData.nbrHeuresOuvrables > 0 
    ? salaireBase / pointageData.nbrHeuresOuvrables 
    : salaireBase / 173.33; // Valeur par défaut pour un mois
  
  // 1 & 2. Rappel Salaire
  const rappelSalaireJ = 0; // À récupérer des rappels de salaire
  const rappelSalaire = 0;  // À récupérer des rappels de salaire
  
  // 3 & 4. Indemnité des Heures Supplémentaires
  const tauxMajorationHS = remuneration.tauxMajorationHS || 1.4; // Par défaut 40% de majoration
  const indemniteHSJ = pointageData.nbrHeuresSupplementaires || 0;
  const indemniteHS = indemniteHSJ * tauxHoraire * tauxMajorationHS;
  
  // 5 & 6. Jours déjà travaillé
  const joursDejaTravaileJ = pointageData.nbrJoursTravailles || 0;
  const joursDejaTravaile = joursDejaTravaileJ * tauxJournalier;
  
  // 7 & 8. Indemnité du Congés/Récup
  const indemniteCongesRecupJ = 0; // À récupérer de la gestion des congés
  const indemniteCongesRecup = 0;  // À récupérer de la gestion des congés
  
  // 9 & 10. Indemnité du Congés Annuel
  const indemniteCongesAnnuelJ = 0; // À récupérer de la gestion des congés
  const indemniteCongesAnnuel = 0;  // À récupérer de la gestion des congés
  
  // 11. Base des Indemnités et les Primes
  const baseIndemnitesPrimes = salaireBase;
  
  // 12 & 13. Indemnité Expérience Professionnelle
  const indExpProfPct = primesIndemnites.indemniteExperiencePct || 10; // Par défaut 10%
  const indemniteExpProf = (baseIndemnitesPrimes * indExpProfPct) / 100;
  
  // 14 & 15. Indemnité de Nuisance
  const indemniteNuisancePct = primesIndemnites.indemniteNuisancePct || 5; // Par défaut 5%
  const indemniteNuisance = (baseIndemnitesPrimes * indemniteNuisancePct) / 100;
  
  // 16 & 17. Prime de Responsabilité
  const primeResponsabilitePct = employeeData.primeResponsabilitePct || 
                                primesIndemnites.primeResponsabilitePct || 0;
  const primeResponsabilite = (baseIndemnitesPrimes * primeResponsabilitePct) / 100;
  
  // 18 & 19. Prime de Risque
  const primeRisquePct = primesIndemnites.primeRisquePct || 3; // Par défaut 3%
  const primeRisque = (baseIndemnitesPrimes * primeRisquePct) / 100;
  
  // 20 & 21. Prime de Conduite
  const primeConduiteJ = primesIndemnites.primeConduiteJournaliere || 0;
  const primeConduite = primeConduiteJ * joursDejaTravaileJ;
  
  // 22 & 23. Prime de Rendement Collectif
  const primeRendementCollectifF = primesIndemnites.facteurRendementCollectif || 1;
  const primeRendementCollectif = primesIndemnites.montantRendementCollectif || 0;
  
  // 24 & 25. Prime de Rendement Individuel
  const primeRendementIndividuelF = employeeData.facteurRendementIndividuel || 1;
  const primeRendementIndividuel = (primesIndemnites.montantRendementIndividuel || 0) * primeRendementIndividuelF;
  
  // 26. Total Salaire Cotisable/Imposable
  const totalSCI = rappelSalaire + indemniteHS + joursDejaTravaile + 
                  indemniteCongesRecup + indemniteCongesAnnuel + 
                  indemniteExpProf + indemniteNuisance + 
                  primeResponsabilite + primeRisque + 
                  primeConduite + primeRendementCollectif + primeRendementIndividuel;
  
  return {
    rappelSalaireJ,
    rappelSalaire,
    indemniteHSJ,
    indemniteHS,
    joursDejaTravaileJ,
    joursDejaTravaile,
    indemniteCongesRecupJ,
    indemniteCongesRecup,
    indemniteCongesAnnuelJ,
    indemniteCongesAnnuel,
    baseIndemnitesPrimes,
    indExpProfPct,
    indemniteExpProf,
    indemniteNuisancePct,
    indemniteNuisance,
    primeResponsabilitePct,
    primeResponsabilite,
    primeRisquePct,
    primeRisque,
    primeConduiteJ,
    primeConduite,
    primeRendementCollectifF,
    primeRendementCollectif,
    primeRendementIndividuelF,
    primeRendementIndividuel,
    totalSCI
  };
}; 

/**
 * Calcule les éléments du Salaire Imposable Non Cotisable selon les paramètres définis
 * Utilise les données de l'employé, du pointage et les paramètres de calcul
 * 
 * @param {Object} employee - Données de l'employé
 * @param {Object} pointage - Données de pointage (jours travaillés, heures supplémentaires, etc.)
 * @param {Object} settings - Paramètres de calcul (primes, indemnités, formules)
 * @returns {Object} - Objet contenant tous les éléments du Salaire Imposable Non Cotisable
 */
export const calculerSalaireImposableNonCotisable = (employee, pointage, settings) => {
  // Valeurs par défaut si non fournies
  const employeeData = employee || {};
  const pointageData = pointage || {
    nbrJoursMois: 30,
    nbrJoursOuvrables: 22,
    nbrJoursTravailles: 22,
    nbrJoursSupplementaires: 0,
    nbrHeuresSupplementaires: 0
  };
  const settingsData = settings || { remuneration: {} };
  
  // Récupérer les paramètres de rémunération
  const remuneration = settingsData.remuneration || {};
  const primesIndemnites = remuneration.primesIndemnites || {};
  
  // 1. Indemnité de Transport/J (Indicator) - Valeur journalière indicative
  const indemniteTransportJIndicator = primesIndemnites.indemniteTransportJIndicator || 300;
  
  // 2. Indemnité de Transport/J (Normal) - Valeur journalière normale
  const indemniteTransportJNormal = primesIndemnites.indemniteTransportJNormal || 500;
  
  // 3. Indemnité de Transport - Calculée en fonction des jours travaillés
  const indemniteTransport = indemniteTransportJNormal * pointageData.nbrJoursTravailles;
  
  // 4. Indemnité de Transport exceptionnel/F - Facteur pour transport exceptionnel
  const indemniteTransportExceptionnelF = employeeData.indemniteTransportExceptionnelF || 
                                        primesIndemnites.indemniteTransportExceptionnelF || 2;
  
  // 5. Indemnité de Transport exceptionnel - Montant fixe ou calculé avec le facteur
  const indemniteTransportExceptionnel = primesIndemnites.indemniteTransportExceptionnel || 
                                       (indemniteTransportJNormal * indemniteTransportExceptionnelF);
  
  // 6. Prime de Panier des Jours Travaillés / J - Valeur journalière
  const primePanierJoursTravaillesJ = primesIndemnites.primePanierJournaliere || 700;
  
  // 7. Prime de Panier des Jours Travaillés - Calculée en fonction des jours travaillés
  const primePanierJoursTravailles = primePanierJoursTravaillesJ * pointageData.nbrJoursTravailles;
  
  // 8. Salissire / F - Facteur pour prime de salissure
  const salissireF = employeeData.salissireF || primesIndemnites.salissireF || 1;
  
  // 9. Salissire - Montant de la prime de salissure
  const salissire = (primesIndemnites.montantSalissure || 1500) * salissireF;
  
  // 10. Total SI/NC - Total du Salaire Imposable Non Cotisable
  const totalSINC = indemniteTransport + 
                  indemniteTransportExceptionnel + 
                  primePanierJoursTravailles + 
                  salissire;
  
  return {
    indemniteTransportJIndicator,
    indemniteTransportJNormal,
    indemniteTransport,
    indemniteTransportExceptionnelF,
    indemniteTransportExceptionnel,
    primePanierJoursTravaillesJ,
    primePanierJoursTravailles,
    salissireF,
    salissire,
    totalSINC
  };
}; 

/**
 * Calcule les éléments du récapitulatif des cotisations pour un salarié
 * Inclut les cotisations sociales, l'IRG et le salaire net
 * 
 * @param {Object} employee - Données de l'employé
 * @param {Object} salaireBrutTotal - Salaire brut total (somme des éléments cotisables)
 * @param {Object} settings - Paramètres de calcul (taux de cotisations, barème IRG, etc.)
 * @returns {Object} - Objet contenant tous les éléments du récapitulatif des cotisations
 */
export const calculerRecapitulatifCotisations = (employee, salaireBrutTotal, settings) => {
  const employeeData = employee || {};
  const salaireBrut = salaireBrutTotal || 0;
  const settingsData = settings || { remuneration: {} };

  if (isSaudiBrand()) {
    const summary = calculateSaudiPayrollSummary(employeeData, salaireBrut, settingsData);
    return {
      salaireBrut: summary.salaireBrut,
      retenueSSSalarie: summary.retenueSSSalarie,
      retenueSSEmployeur: summary.retenueSSEmployeur,
      retenueGOSISalarie: summary.retenueGOSISalarie,
      retenueGOSIEmployeur: summary.retenueGOSIEmployeur,
      retenueSANEDSalarie: summary.retenueSANEDSalarie,
      retenueSANEDEmployeur: summary.retenueSANEDEmployeur,
      cacobatCongesPayes: summary.provisionEOSB,
      congesPayes: summary.congesPayes,
      salaireImposable: summary.salaireImposable,
      brmRetenueIRG: 0,
      retenueIRG: 0,
      netComptable: summary.netComptable,
      provisionEOSB: summary.provisionEOSB,
    };
  }

  const remuneration = settingsData.remuneration || {};
  
  // Taux de cotisations sociales (valeurs par défaut si non définies dans les paramètres)
  const tauxCotisations = {
    securiteSocialeSalarie: remuneration.tauxSSEmploye || 9, // 9% par défaut
    securiteSocialeEmployeur: remuneration.tauxSSEmployeur || 26, // 26% par défaut
    cacobatCongesPayes: remuneration.tauxCacobatCongesPayes || 12.21, // 12.21% par défaut
    congesPayes: remuneration.tauxCongesPayes || 12.21 // 12.21% par défaut
  };
  
  // Options pour le calcul des cotisations sociales
  const options = {
    estEtranger: employeeData.estEtranger || false,
    modeCNAS: employeeData.modeCNAS || 0, // 0 = Standard
    cotiseCacobatph: true // Par défaut, on cotise au CACOBAT pour les congés payés
  };
  
  // 1. Calcul des cotisations sociales
  const retenueSSSalarie = (salaireBrut * tauxCotisations.securiteSocialeSalarie) / 100;
  const retenueSSEmployeur = (salaireBrut * tauxCotisations.securiteSocialeEmployeur) / 100;
  const cacobatCongesPayes = (salaireBrut * tauxCotisations.cacobatCongesPayes) / 100;
  const congesPayes = (salaireBrut * tauxCotisations.congesPayes) / 100;
  
  // 2. Calcul du salaire imposable
  const salaireImposable = salaireBrut - retenueSSSalarie;
  
  // 3. Calcul de la base de retenue IRG (abattement de 10%)
  const brmRetenueIRG = Math.round(salaireImposable * 0.9); // Arrondi à l'entier le plus proche
  
  // 4. Calcul de l'IRG selon le barème fiscal
  let retenueIRG = 0;
  const baremeIRG = remuneration.irg?.bareme || [
    { min: 0, max: 20000, taux: 0, deduction: 0 },
    { min: 20001, max: 40000, taux: 20, deduction: 4000 },
    { min: 40001, max: 80000, taux: 30, deduction: 8000 },
    { min: 80001, max: 160000, taux: 35, deduction: 12000 },
    { min: 160001, max: 320000, taux: 40, deduction: 20000 },
    { min: 320001, max: Infinity, taux: 45, deduction: 36000 }
  ];
  
  // Options pour le calcul de l'IRG
  const optionsIRG = {
    abattementSud: employeeData.abattementSud || 0,
    abattementHandicape: employeeData.abattementHandicape || 0,
    exonere: employeeData.exonereIRG || false,
    abattementAutre: employeeData.abattementAutreIRG || 0
  };
  
  // Calculer l'IRG avec la fonction dédiée
  retenueIRG = calculerIRG(salaireImposable, 1, baremeIRG, optionsIRG);
  
  // 5. Calcul du net comptable
  const netComptable = salaireImposable - retenueIRG;
  
  // Retourner l'ensemble des données calculées
  return {
    salaireBrut,
    retenueSSSalarie,
    retenueSSEmployeur,
    cacobatCongesPayes,
    congesPayes,
    salaireImposable,
    brmRetenueIRG,
    retenueIRG,
    netComptable
  };
}; 

/**
 * Calcule les éléments du Salaire Non Cotisable/Non Imposable (4)
 * Ces indemnités et primes ne sont ni soumises aux cotisations sociales ni à l'IRG
 * 
 * @param {Object} employee - Données de l'employé
 * @param {Object} pointage - Données de pointage (jours travaillés, etc.)
 * @param {Object} settings - Paramètres de calcul (primes, indemnités, formules)
 * @returns {Object} - Objet contenant tous les éléments du Salaire Non Cotisable/Non Imposable
 */
export const calculerSalaireNonCotisableNonImposable = (employee, pointage, settings) => {
  // Valeurs par défaut si non fournies
  const employeeData = employee || {};
  const pointageData = pointage || {
    nbrJoursMois: 30,
    nbrJoursOuvrables: 22,
    nbrJoursTravailles: 22
  };
  const settingsData = settings || { remuneration: { primesIndemnites: {} } };
  
  // Récupérer les paramètres de rémunération
  const primesIndemnites = settingsData.remuneration?.primesIndemnites || {};
  
  // 1. Indemnité Forfaitaire Région Isolée
  const indemnForfRegionIsoleeJ = primesIndemnites.indemnForfRegionIsoleeJ || 500;
  const indemnForfRegionIsoleeJrsJ = pointageData.nbrJoursTravailles || 22;
  const indemnForfRegionIsolee = indemnForfRegionIsoleeJ * indemnForfRegionIsoleeJrsJ;
  
  // 2. Rappel Indemnité Forfaitaire Région Isolée
  const rappelIndemnForfRegionIsoleeJ = primesIndemnites.rappelIndemnForfRegionIsoleeJ || 0;
  const rappelIndemnForfRegionIsolee = rappelIndemnForfRegionIsoleeJ * (pointageData.nbrJoursRappel || 0);
  
  // 3. Prime de l'Aid
  // Facteur: 1 = actif, 0 = inactif
  const primeAidF = primesIndemnites.primeAidF || 0;
  const primeAid = primeAidF * (primesIndemnites.montantPrimeAid || 10000);
  
  // 4. Prime de Mariage
  const primeMariageF = employeeData.mariageCeMois ? 1 : 0;
  const primeMariage = primeMariageF * (primesIndemnites.montantPrimeMariage || 15000);
  
  // 5. Indemnité de Décès
  const indemniteDecesJ = employeeData.deuilCeMois ? (primesIndemnites.joursIndemniteDeces || 3) : 0;
  const indemniteDeces = indemniteDecesJ * (employeeData.salaireJournalier || 0);
  
  // 6. Indemnité Véhicule
  const indemniteVehiculeJ = employeeData.vehiculePersonnel ? (primesIndemnites.joursIndemniteVehicule || 0) : 0;
  const indemniteVehiculeJ2 = primesIndemnites.montantIndemniteVehiculeJournalier || 0;
  const indemniteVehicule = indemniteVehiculeJ * indemniteVehiculeJ2;
  
  // 7. Indemnité de Remplacement
  const indemniteRemplacementJ = employeeData.joursRemplacement || 0;
  const indemniteRemplacement = indemniteRemplacementJ * (primesIndemnites.montantIndemniteRemplacement || 0);
  
  // 8. Communications Téléphoniques
  const communicationsTelephoniqueF = employeeData.fonctionCommunication ? 1 : 0;
  const communicationsTelephonique = communicationsTelephoniqueF * (primesIndemnites.montantCommunicationsTelephonique || 3000);
  
  // 9. Indemnité Absence Autorisée Payée
  const indemnAOPJ = employeeData.joursAbsenceAutoriseePaye || 0;
  const indemnAOP = indemnAOPJ * (employeeData.salaireJournalier || 0);
  
  // 10. Prime de Fidélité
  const primeFideliteF = (employeeData.anneesAnciennete || 0) >= (primesIndemnites.anneesAnciennetePrimeFidelite || 5) ? 1 : 0;
  const primeFidelite = primeFideliteF * (primesIndemnites.montantPrimeFidelite || 0);
  
  // 11. Prime de Naissance
  const primeNaissanceF = employeeData.naissanceCeMois ? 1 : 0;
  const primeNaissance = primeNaissanceF * (primesIndemnites.montantPrimeNaissance || 10000);
  
  // 12. Remboursement Outillage
  const remboursementOutillageF = employeeData.remboursementOutillageCeMois ? 1 : 0;
  const remboursementOutillage = remboursementOutillageF * (employeeData.montantRemboursementOutillage || 0);
  
  // 13. Prime Exceptionnelle
  const primeExceptionnelF = employeeData.primeExceptionnelleCeMois ? 1 : 0;
  const primeExceptionnel = primeExceptionnelF * (employeeData.montantPrimeExceptionnelle || 0);
  
  // Calcul du total
  const totalSNCNI = 
    indemnForfRegionIsolee +
    rappelIndemnForfRegionIsolee +
    primeAid +
    primeMariage +
    indemniteDeces +
    indemniteVehicule +
    indemniteRemplacement +
    communicationsTelephonique +
    indemnAOP +
    primeFidelite +
    primeNaissance +
    remboursementOutillage +
    primeExceptionnel;
  
  // Retourner l'ensemble des données calculées
  return {
    indemnForfRegionIsoleeJ,
    indemnForfRegionIsoleeJrsJ,
    indemnForfRegionIsolee,
    rappelIndemnForfRegionIsoleeJ,
    rappelIndemnForfRegionIsolee,
    primeAidF,
    primeAid,
    primeMariageF,
    primeMariage,
    indemniteDecesJ,
    indemniteDeces,
    indemniteVehiculeJ,
    indemniteVehiculeJ2,
    indemniteVehicule,
    indemniteRemplacementJ,
    indemniteRemplacement,
    communicationsTelephoniqueF,
    communicationsTelephonique,
    indemnAOPJ,
    indemnAOP,
    primeFideliteF,
    primeFidelite,
    primeNaissanceF,
    primeNaissance,
    remboursementOutillageF,
    remboursementOutillage,
    primeExceptionnelF,
    primeExceptionnel,
    totalSNCNI
  };
}; 

/**
 * Calcule les éléments de la section Frais de Mission et Avances et Retenue
 * Cette section inclut les frais de mission, les avances sur salaire et diverses retenues
 * 
 * @param {Object} employee - Données de l'employé
 * @param {Object} pointage - Données de pointage (jours travaillés, etc.)
 * @param {Object} settings - Paramètres de calcul (taux, montants, etc.)
 * @param {Object} avancesRetenues - Données mensuelles d'avances et retenues (optionnel)
 * @returns {Object} - Objet contenant tous les éléments de la section Frais de Mission et Avances et Retenue
 */
export const calculerFraisMissionAvancesRetenues = (employee, pointage, settings, avancesRetenues = null) => {
  // Valeurs par défaut si non fournies
  const employeeData = employee || {};
  const pointageData = pointage || {
    nbrJoursMois: 30,
    nbrJoursOuvrables: 22,
    nbrJoursTravailles: 22
  };
  const settingsData = settings || { remuneration: { fraisMissionAvances: {} } };
  
  // Récupérer les paramètres de rémunération
  const fraisMissionAvances = settingsData.remuneration?.fraisMissionAvances || {};
  
  // Utiliser les données mensuelles d'avances/retenues si disponibles, sinon utiliser les données de l'employé
  const avancesRetenuesData = avancesRetenues || {
    avanceSalaire: employeeData.avanceSalaire || 0,
    retenueAcompte: employeeData.retenueAcompte || 0,
    retenuePret: employeeData.retenuePret || 0,
    joursAbsenceNonJustifiee: employeeData.joursAbsenceNonJustifiee || 0,
    joursAbsenceMaladieNonIndemnisee: employeeData.joursAbsenceMaladieNonIndemnisee || 0,
    retenueOppositionSalaire: employeeData.retenueOppositionSalaire || 0,
    retenuePensionAlimentaire: employeeData.retenuePensionAlimentaire || 0,
    retenueTropPercu: employeeData.retenueTropPercu || 0
  };
  
  // 1. Frais de Mission
  const fraisMissionJ = employeeData.fraisMissionJournalier || fraisMissionAvances.fraisMissionJournalier || 0;
  const fraisMissionJrs = employeeData.joursEnMission || 0;
  const fraisMission = fraisMissionJ * fraisMissionJrs;
  
  // 2. Frais de Déplacement
  const fraisDeplacementJ = employeeData.fraisDeplacementJournalier || fraisMissionAvances.fraisDeplacementJournalier || 0;
  const fraisDeplacementJrs = employeeData.joursEnDeplacement || 0;
  const fraisDeplacement = fraisDeplacementJ * fraisDeplacementJrs;
  
  // 3. Avance sur Salaire
  const avanceSalaire = avancesRetenuesData.avanceSalaire;
  
  // 4. Retenue Acompte
  const retenueAcompte = avancesRetenuesData.retenueAcompte;
  
  // 5. Retenue Prêt
  const retenuePret = avancesRetenuesData.retenuePret;
  
  // 6. Retenue Absence Non Justifiée
  const retenueANJJ = avancesRetenuesData.joursAbsenceNonJustifiee;
  const salaireJournalier = employeeData.salaireBase ? employeeData.salaireBase / pointageData.nbrJoursOuvrables : 0;
  const retenueANJ = retenueANJJ * salaireJournalier;
  
  // 7. Retenue Absence Maladie Non Indemnisée
  const retenueAMNIJ = avancesRetenuesData.joursAbsenceMaladieNonIndemnisee;
  const retenueAMNI = retenueAMNIJ * salaireJournalier;
  
  // 8. Retenue Opposition sur Salaire
  const retenueOppositionSalaire = avancesRetenuesData.retenueOppositionSalaire;
  
  // 9. Retenue Pension Alimentaire
  const retenuePensionAlimentaire = avancesRetenuesData.retenuePensionAlimentaire;
  
  // 10. Retenue Trop-perçu
  const retenueTropPercu = avancesRetenuesData.retenueTropPercu;
  
  // Calcul des totaux
  const totalFraisMission = fraisMission + fraisDeplacement;
  const totalAvances = avanceSalaire;
  const totalRetenues = retenueAcompte + retenuePret + retenueANJ + retenueAMNI + retenueOppositionSalaire + retenuePensionAlimentaire + retenueTropPercu;
  
  // Retourner l'ensemble des données calculées
  return {
    fraisMissionJ,
    fraisMissionJrs,
    fraisMission,
    fraisDeplacementJ,
    fraisDeplacementJrs,
    fraisDeplacement,
    avanceSalaire,
    retenueAcompte,
    retenuePret,
    retenueANJJ,
    retenueANJ,
    retenueAMNIJ,
    retenueAMNI,
    retenueOppositionSalaire,
    retenuePensionAlimentaire,
    retenueTropPercu,
    totalFraisMission,
    totalAvances,
    totalRetenues
  };
}; 

/**
 * Calcule les éléments de la section Préjudice
 * Cette section inclut les indemnités de licenciement, de départ et autres préjudices
 * 
 * @param {Object} employee - Données de l'employé
 * @param {Object} pointage - Données de pointage (jours travaillés, etc.)
 * @param {Object} settings - Paramètres de calcul (taux, montants, etc.)
 * @param {Object} prejudiceData - Données spécifiques de préjudice (optionnel)
 * @returns {Object} - Objet contenant tous les éléments de la section Préjudice
 */
export const calculerPrejudice = (employee, pointage, settings, prejudiceData = null) => {
  // Valeurs par défaut si non fournies
  const employeeData = employee || {};
  const pointageData = pointage || {
    nbrJoursMois: 30,
    nbrJoursOuvrables: 22,
    nbrJoursTravailles: 22
  };
  const settingsData = settings || { remuneration: { prejudice: {} } };
  
  // Récupérer les paramètres de rémunération
  const prejudiceSettings = settingsData.remuneration?.prejudice || {};
  
  // Utiliser les données spécifiques de préjudice si disponibles, sinon utiliser les données de l'employé
  const prejudiceSpecificData = prejudiceData || {
    indemniteLicenciement: employeeData.indemniteLicenciement || 0,
    indemniteDepart: employeeData.indemniteDepart || 0,
    indemnitePreavise: employeeData.indemnitePreavise || 0,
    indemniteCompensatriceConges: employeeData.indemniteCompensatriceConges || 0,
    indemniteCDD: employeeData.indemniteCDD || 0,
    dommagesInterets: employeeData.dommagesInterets || 0,
    rappelSalaire: employeeData.rappelSalaire || 0,
    prejudiceMoral: employeeData.prejudiceMoral || 0,
    prejudiceFinancier: employeeData.prejudiceFinancier || 0,
    autresIndemnites: employeeData.autresIndemnites || 0
  };
  
  // 1. Indemnité de Licenciement
  const indemniteLicenciement = prejudiceSpecificData.indemniteLicenciement;
  
  // 2. Indemnité de Départ Volontaire
  const indemniteDepart = prejudiceSpecificData.indemniteDepart;
  
  // 3. Indemnité de Préavis Non Effectué
  const indemnitePreavise = prejudiceSpecificData.indemnitePreavise;
  
  // 4. Indemnité Compensatrice de Congés Payés
  const indemniteCompensatriceConges = prejudiceSpecificData.indemniteCompensatriceConges;
  
  // 5. Indemnité de Fin de Contrat (CDD)
  const indemniteCDD = prejudiceSpecificData.indemniteCDD;
  
  // 6. Dommages et Intérêts
  const dommagesInterets = prejudiceSpecificData.dommagesInterets;
  
  // 7. Rappel de Salaire
  const rappelSalaire = prejudiceSpecificData.rappelSalaire;
  
  // 8. Préjudice Moral
  const prejudiceMoral = prejudiceSpecificData.prejudiceMoral;
  
  // 9. Préjudice Financier
  const prejudiceFinancier = prejudiceSpecificData.prejudiceFinancier;
  
  // 10. Autres Indemnités
  const autresIndemnites = prejudiceSpecificData.autresIndemnites;
  
  // Calcul du total
  const totalPrejudice = 
    indemniteLicenciement +
    indemniteDepart +
    indemnitePreavise +
    indemniteCompensatriceConges +
    indemniteCDD +
    dommagesInterets +
    rappelSalaire +
    prejudiceMoral +
    prejudiceFinancier +
    autresIndemnites;
  
  // Retourner l'ensemble des données calculées
  return {
    indemniteLicenciement,
    indemniteDepart,
    indemnitePreavise,
    indemniteCompensatriceConges,
    indemniteCDD,
    dommagesInterets,
    rappelSalaire,
    prejudiceMoral,
    prejudiceFinancier,
    autresIndemnites,
    totalPrejudice
  };
}; 
